import { ChevronDown, ChevronUp, Clock } from 'react-feather';
import { Fragment, memo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import groupBy from 'lodash/groupBy';
import sortBy from 'lodash/sortBy';
import uniqBy from 'lodash/uniqBy';

import {
  GetEventRegistrationsStatsQuery as GetEventRegistrationsStats,
} from '__generated__/graphql';
import { ProgressBar, StatsListContainer, StatsListFooter, StatsListRow } from './Layout';
import { RevenueColumn, RevenueColumnOption, SalesColumn, SalesColumnOption } from './TicketStats';
import UI from '../../common/UI';
import useLocale from '../../common/useLocale';
import useMediaDevice from '../../common/useMediaDevice';
import useTimeSlotFormatter from '../../common/Tickets/useTimeSlotFormatter';

type Event = GetEventRegistrationsStats['event'];
type Ticket = Event['tickets'][0];
type BaseTimeSlot = Ticket['time_slots'][0];

interface TimeSlot extends BaseTimeSlot {
  ticket: Ticket;
}

type TimeSlotGroup = TimeSlot[];
type DayGoup = TimeSlotGroup[];

export interface TimeSlotStatsProps {
  tickets: Ticket[];
  salesColumn: SalesColumnOption;
  revenueColumn: RevenueColumnOption;
}

const TimeSlotStats = memo(({ tickets, salesColumn, revenueColumn } : TimeSlotStatsProps) => {
  const { t } = useTranslation();
  const device = useMediaDevice();

  const timeSlots = sortBy(tickets.reduce((timeSlots, ticket) => [
    ...timeSlots,
    ...ticket.time_slots.map((timeSlot) => ({ ...timeSlot, ticket })),
  ], []), ['start_date', 'start_time']);

  // Group time slots by start date, start time and title.
  // Sort each group of time slots by ticket position.
  // Then, group again by the start date of the time slot.
  const groupedTimeSlots: DayGoup[] = Object.values(groupBy(
    Object.values(groupBy(
      timeSlots || [],
      (timeSlot) => `${timeSlot.start_date} ${timeSlot.start_time} ${timeSlot.title}`,
    )).map((timeSlots: TimeSlot[]) => sortBy(timeSlots, (timeSlot) => timeSlot.ticket.position)),
    (group) => group[0].start_date,
  ));

  const multiDate = uniqBy(timeSlots || [], 'start_date').length > 1;
  const limit = 5;
  const [showingAll, setShowingAll] = useState(false);

  const visibleTimeSlotGroups: DayGoup[] = multiDate ? (
    // When there are multiple dates, limit the number of days
    showingAll ? groupedTimeSlots : groupedTimeSlots.slice(0, limit)
  ) : (
    // When there is one date, limit the timeslots within that one day
    showingAll ? groupedTimeSlots : [groupedTimeSlots[0].slice(0, limit)]
  );

  const visibleGroupLength = multiDate ? groupedTimeSlots.length : groupedTimeSlots[0].length;

  const totals = timeSlots.reduce((totals, timeSlot) => ({
    sold_count: totals.sold_count + timeSlot.sold_count,
    invitation_reservations_count: totals.invitation_reservations_count + timeSlot.invitation_reservations_count,
    revenue: totals.revenue + timeSlot.revenue,
    remaining_amount: totals.remaining_amount + timeSlot.remaining_amount,
    active_purchases_count: totals.active_purchases_count + timeSlot.active_purchases_count,
    accepted_purchases_amount: totals.accepted_purchases_amount + timeSlot.accepted_purchases_amount,
    capacity: totals.capacity === null || timeSlot.capacity === null ? null : totals.capacity + timeSlot.capacity,
  }), {
    sold_count: 0,
    invitation_reservations_count: 0,
    revenue: 0,
    remaining_amount: 0,
    active_purchases_count: 0,
    accepted_purchases_amount: 0,
    capacity: 0 satisfies number | null,
  });

  return (
    <>
      <StatsListContainer>
        {visibleTimeSlotGroups.map((dayGroup) => (
          <TimeSlotDailySummaryRow
            dayGroup={dayGroup}
            multiDate={multiDate}
            salesColumn={salesColumn}
            revenueColumn={revenueColumn}
            key={dayGroup[0][0].start_date}
          />
        ))}
        {(showingAll || visibleGroupLength <= limit) && (
          <StatsListRow>
            <UI.Div sc={{ fontWeight: 500 }}>
              {t('totals')}
            </UI.Div>
            {!device.isSmall && <UI.Div />}
            <SalesColumn activeColumn={salesColumn} source={totals} hideCapacity />
            <RevenueColumn activeColumn={revenueColumn} source={totals} />
          </StatsListRow>
        )}
      </StatsListContainer>
      {visibleGroupLength > limit && (
        <StatsListFooter>
          {!showingAll && (
            <UI.Button
              onClick={() => setShowingAll(true)}
              sc={{ blank: true, size: 'xs', mr: 2 }}
            >
              {t('show_all')}
              {' '}
              <UI.Span sc={{ muted: true }}>
                {`(${visibleGroupLength})`}
              </UI.Span>
              {' '}
              <UI.Icon sc={{ muted: true }}>
                <ChevronDown />
              </UI.Icon>
            </UI.Button>
          )}
          {showingAll && (
            <UI.Button
              onClick={() => setShowingAll(false)}
              sc={{ blank: true, size: 'xs', mr: 2 }}
            >
              {t('show_less')}
              {' '}
              <UI.Icon sc={{ muted: true }}>
                <ChevronUp />
              </UI.Icon>
            </UI.Button>
          )}
        </StatsListFooter>
      )}
    </>
  );
});

interface TimeSlotDailySummaryRowProps {
  dayGroup: DayGoup;
  multiDate: boolean;
  salesColumn: SalesColumnOption;
  revenueColumn: RevenueColumnOption;
}

const TimeSlotDailySummaryRow = ({ dayGroup, multiDate, salesColumn, revenueColumn }: TimeSlotDailySummaryRowProps) => {
  const { t } = useTranslation();
  const { formatDate, parseDate } = useLocale();
  const device = useMediaDevice();

  const forDays = (reducer: (sum: number, timeSlot: TimeSlot) => number) => (
    dayGroup.reduce((sum, timeSlots) => sum + timeSlots.reduce(reducer, 0), 0)
  );

  const purchasesCount = forDays((sum, timeSlot) => sum + timeSlot.sold_count);
  const reservationsCount = forDays((sum, timeSlot) => sum + timeSlot.invitation_reservations_count);
  const capacity = forDays((sum, timeSlot) => (
    sum === null ? sum : (timeSlot.capacity !== null ? sum + timeSlot.capacity : null)
  ));

  const sales = {
    sold_count: purchasesCount,
    invitation_reservations_count: reservationsCount,
    capacity,
  };

  const revenue = {
    revenue: forDays((sum, timeSlot) => sum + timeSlot.revenue),
    remaining_amount: forDays((sum, timeSlot) => sum + timeSlot.remaining_amount),
    accepted_purchases_amount: forDays((sum, timeSlot) => sum + timeSlot.accepted_purchases_amount),
    active_purchases_count: forDays((sum, timeSlot) => sum + timeSlot.active_purchases_count),
  };

  const [showingTimeSlotStats, setShowingTimeSlotStats] = useState(false);
  const toggleTimeSlotStats = () => setShowingTimeSlotStats((showing) => !showing);

  const title = formatDate(parseDate(`${dayGroup[0][0].start_date} 00:00:00`), {
    format: 'display_date',
  });

  return (
    <>
      {multiDate && (
        <StatsListRow>
          <UI.FlexContainer style={{ minWidth: 0 }}>
            <UI.Truncate>
              {title}
            </UI.Truncate>
            <UI.Span>
              <UI.Button
                sc={{ blank: true, size: 'xs', my: -0.75, ml: 1 }}
                onClick={() => toggleTimeSlotStats()}
                aria-label={t('toggle_name', { name: title })}
              >
                <UI.Icon>
                  {showingTimeSlotStats && <ChevronUp />}
                  {!showingTimeSlotStats && <ChevronDown />}
                </UI.Icon>
              </UI.Button>
            </UI.Span>
          </UI.FlexContainer>
          {!device.isSmall && (
            <UI.Div>
              {capacity > 0 && (
                <ProgressBar
                  count={purchasesCount}
                  capacity={capacity}
                  secondary={reservationsCount}
                  tooltip={t('sales_plus_reservations')}
                />
              )}
            </UI.Div>
          )}
          <SalesColumn activeColumn={salesColumn} source={sales} />
          <RevenueColumn activeColumn={revenueColumn} source={revenue} />
        </StatsListRow>
      )}
      {(showingTimeSlotStats || !multiDate) && dayGroup.map((timeSlots) => (
        <TimeSlotRow
          timeSlots={timeSlots}
          multiDate={multiDate}
          salesColumn={salesColumn}
          revenueColumn={revenueColumn}
          key={`group-${timeSlots[0].id}`}
        />
      ))}
    </>
  );
};

interface TimeSlotRowProps {
  timeSlots: TimeSlotGroup;
  multiDate: boolean;
  salesColumn: SalesColumnOption;
  revenueColumn: RevenueColumnOption;
}

const TimeSlotRow = ({ timeSlots, multiDate, salesColumn, revenueColumn }: TimeSlotRowProps) => {
  const { t } = useTranslation();
  const formatTimeSlot = useTimeSlotFormatter();
  const device = useMediaDevice();

  const timeSlot = timeSlots[0];
  const [showingExtraStats, setShowingExtraStats] = useState(false);

  const purchasesCount = timeSlots.reduce((sum, timeSlot) => sum + timeSlot.sold_count, 0);
  const reservationsCount = timeSlots.reduce((sum, timeSlot) => sum + timeSlot.invitation_reservations_count, 0);
  const capacity = timeSlots.reduce((sum, timeSlot) => (
    sum === null ? sum : (timeSlot.capacity !== null ? sum + timeSlot.capacity : null)
  ), 0);

  const sales = {
    sold_count: purchasesCount,
    invitation_reservations_count: reservationsCount,
    capacity,
  };

  const revenue = {
    revenue: timeSlots.reduce((sum, timeSlot) => sum + timeSlot.revenue, 0),
    remaining_amount: timeSlots.reduce((sum, timeSlot) => sum + timeSlot.remaining_amount, 0),
    accepted_purchases_amount: timeSlots.reduce((sum, timeSlot) => sum + timeSlot.accepted_purchases_amount, 0),
    active_purchases_count: timeSlots.reduce((sum, timeSlot) => sum + timeSlot.active_purchases_count, 0),
  };

  const toggleExtraStats = () => setShowingExtraStats((showing) => !showing);

  return (
    <>
      <StatsListRow sc={{ background: multiDate ? 'gray.50' : undefined }}>
        <UI.FlexContainer style={{ minWidth: 0 }}>
          <UI.Truncate sc={{ pl: multiDate ? 4 : 0 }}>
            {multiDate && (
              <UI.Icon sc={{ mr: 1 }}>
                <Clock />
              </UI.Icon>
            )}
            <UI.Delimit>
              {formatTimeSlot({ ...timeSlot, multi_date: false })}
              {timeSlot.title}
            </UI.Delimit>
          </UI.Truncate>
          <UI.Span>
            <UI.Button
              sc={{ blank: true, size: 'xs', my: -0.75, ml: 1 }}
              onClick={() => toggleExtraStats()}
              aria-label={t('toggle_name', { name: formatTimeSlot({ ...timeSlot, multi_date: false }) })}
            >
              <UI.Icon>
                {showingExtraStats && <ChevronUp />}
                {!showingExtraStats && <ChevronDown />}
              </UI.Icon>
            </UI.Button>
          </UI.Span>
        </UI.FlexContainer>
        {!device.isSmall && (
          <UI.Div>
            {capacity > 0 && (
              <ProgressBar
                count={purchasesCount}
                capacity={capacity}
                secondary={reservationsCount}
                tooltip={t('sales_plus_reservations')}
              />
            )}
          </UI.Div>
        )}
        <SalesColumn activeColumn={salesColumn} source={sales} />
        <RevenueColumn activeColumn={revenueColumn} source={revenue} />
      </StatsListRow>
      {showingExtraStats && (
        <ExtraTimeSlotStats
          timeSlots={timeSlots}
          multiDate={multiDate}
          salesColumn={salesColumn}
          revenueColumn={revenueColumn}
        />
      )}
    </>
  );
};

const ExtraTimeSlotStats = ({ timeSlots, multiDate, salesColumn, revenueColumn }: TimeSlotRowProps) => {
  const { t } = useTranslation();
  const device = useMediaDevice();

  return (
    <>
      {timeSlots.map((timeSlot) => (
        <StatsListRow sc={{ background: 'gray.50' }} key={timeSlot.id}>
          <UI.Div sc={{ pl: multiDate ? 8 : 4 }} style={{ minWidth: 0 }}>
            <UI.Truncate>
              <UI.Icon sc={{ mr: 1 }}>
                <UI.Icons.Ticket />
              </UI.Icon>
              {timeSlot.ticket.title}
            </UI.Truncate>
          </UI.Div>
          {!device.isSmall && (
            <UI.Div>
              {timeSlot.capacity > 0 && (
                <ProgressBar
                  count={timeSlot.sold_count}
                  capacity={timeSlot.capacity}
                  secondary={timeSlot.invitation_reservations_count}
                  tooltip={t('sales_plus_reservations')}
                />
              )}
            </UI.Div>
          )}
          <SalesColumn activeColumn={salesColumn} source={timeSlot} />
          <RevenueColumn activeColumn={revenueColumn} source={timeSlot} />
        </StatsListRow>
      ))}
    </>
  );
};

export default TimeSlotStats;
